import React, { useEffect, useState } from "react";
import { Table, Card, Tag, message, Button, Drawer, Typography } from "antd";
import type { ColumnsType } from "antd/es/table";
import { EyeOutlined } from "@ant-design/icons";
import { fetchEmailData_Api } from "../../Api";
import { Voicemail } from "lucide-react";

interface TriggeredEmail {
  id: number;
  datetime: string;
  email_content: string;
  template_name: string;
  user_id: number | null;
  update_date: string;
  is_Send: number;
  username?: string;
}

type TestEmail = any;
const { Text } = Typography;

export const TriggeredEmails: React.FC = () => {
  const [data, setData] = useState<TriggeredEmail[]>([]);
  const [loading, setLoading] = useState<boolean>(false);
  const [open, setOpen] = useState(false);
  const [selectedEmail, setSelectedEmail] = useState<TestEmail | null>(null);

  const normalizeJson = (value: any) => {
    try {
      if (typeof value === "object") {
        if (value.raw_content && typeof value.raw_content === "string") {
          const cleaned = value.raw_content.replace(/\r\n/g, " ");
          return JSON.parse(cleaned);
        }
        return value;
      }

      if (typeof value === "string") {
        const cleaned = value.replace(/\r\n/g, " ");
        return JSON.parse(cleaned);
      }
    } catch {
      return { error: "Unable to parse JSON", raw: value };
    }
  };

  const showDrawer = (record: TriggeredEmail) => {
    const normalized = normalizeJson(record.email_content);
    setSelectedEmail(normalized);
    setOpen(true);
  };

  const onClose = () => {
    setOpen(false);
    setSelectedEmail(null);
  };

  const fetchData = async () => {
    setLoading(true);
    try {
      const res = await fetchEmailData_Api();
      if (res.status === "success") {
        setData(res.data);
      } else {
        message.error("Failed to fetch data");
      }
    } catch (error) {
      console.error("Error fetching data:", error);
      message.error("An error occurred while fetching data");
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    fetchData();
  }, []);

  const columns: ColumnsType<TriggeredEmail> = [
    {
      title: "ID",
      dataIndex: "id",
      key: "id",
      width: 80,
    },
    {
      title: "Date Time",
      dataIndex: "datetime",
      key: "datetime",
      width: 180,
    },
    {
      title: "Template Name",
      dataIndex: "template_name",
      key: "template_name",
      width: 200,
    },
    {
      title: "User",
      dataIndex: "username",
      key: "username",
      width: 150,
      render: (username) => (username ? username : "N/A"),
    },
    {
      title: "User ID",
      dataIndex: "user_id",
      key: "user_id",
      width: 100,
      render: (id) => (id ? id : "N/A"),
    },
    {
      title: "Update Date",
      dataIndex: "update_date",
      key: "update_date",
      width: 180,
    },
    {
      title: "Status",
      dataIndex: "is_Send",
      key: "is_Send",
      width: 120,
      render: (isSend) => (
        <Tag color={isSend == 1 ? "green" : "red"}>
          {isSend == 1 ? "✅ Sent" : "⏳ Pending"}
        </Tag>
      ),
    },

    {
      title: "Action",
      key: "action",
      width: 100,
      render: (_, record) => (
        <Button
          type="default"
          icon={<EyeOutlined />}
          onClick={() => showDrawer(record)}
        ></Button>
      ),
    },
  ];

  return (
    <div className="w-full min-h-screen bg-gray-50 p-6">
        <div className="flex justify-between items-center px-6">
          <h1 className="text-2xl font-semibold">Email Logs</h1>
        </div>

        <Card className="shadow-sm overflow-hidden">
          <Table
            columns={columns}
            dataSource={data}
            loading={loading}
            rowKey="id"
            pagination={{ pageSize: 10 }}
            bordered
            className="overflow-x-auto"
          />
        </Card>

      <Drawer
        title="📄 Email JSON Payload"
        placement="right"
        onClose={onClose}
        open={open}
        width={720}
      >
        {selectedEmail ? (
          <>
            <pre
              style={{
                background: "#0d1117",
                color: "#c9d1d9",
                padding: "16px",
                borderRadius: "8px",
                fontSize: "13px",
                lineHeight: 1.6,
                maxHeight: "75vh",
                overflow: "auto",
                whiteSpace: "pre-wrap",
                wordBreak: "break-word",
                fontFamily: "JetBrains Mono, Fira Code, Consolas, monospace",
              }}
            >
              {JSON.stringify(selectedEmail, null, 2)}
            </pre>
          </>
        ) : (
          <p>No JSON data available</p>
        )}
      </Drawer>
    </div>
  );
};
